import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const OrderList = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Format ID with leading zeros
  // const formatOrderId = (id) => {
  //   return `ORD-${String(id).padStart(3, '0')}`;
  // };

  // Fetch orders from API
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post("/order_list.php", {});

      if (response.data.head.code === 200) {
        // Transform the data
        const transformedData = response.data.body.map(order => ({
          id: order.id,
          // orderId: formatOrderId(order.id),
          orderId: order.id,
          rawId: order.id,
          date: order.order_date,
          customerName: order.customer_name,
          customer_id: order.customer_id,
          sub_total: parseFloat(order.sub_total) || 0,
          gst_percentage: parseFloat(order.gst_percentage) || 0,
          gst_amount: parseFloat(order.gst_amount) || 0,
          grand_total: parseFloat(order.grand_total) || 0,
          status: order.status,
          created_at: order.created_at,
          amount: `₹${parseFloat(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        }));

        setOrders(transformedData);
      } else if (response.data.head.code === 606) {
        setOrders([]);
      } else {
        setError(response.data.head.msg || "Failed to fetch orders");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete order handler
  const handleDelete = async (id, orderId) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderId}?`)) {
      return;
    }

    try {
      const response = await api.post("/order_delete.php", { id });

      if (response.data.head.code === 200) {
        setOrders(orders.filter((o) => o.id !== id));
        alert("Order deleted successfully!");
      } else {
        alert("Failed to delete order: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting order:", err);
      alert("Failed to delete order. Please try again.");
    }
  };

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "");
    if (s === "0") return "status-badge status-pending";
    if (s === "2") return "status-badge status-progress";
    if (s === "1") return "status-badge status-completed";
    // if (s === "cancelled" || s === "rejected") return "status-badge status-rejected";
    // if (s === "shipped") return "status-badge status-approved";
    return "status-badge status-default";
  };
const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};


  // Filter orders based on search, date range, and status
  const filteredOrders = orders.filter((order) => {
    console.log("fromDate", fromDate);
    if (!search && !fromDate && !toDate && !statusFilter) return true;

    const query = search.toLowerCase();
    // const formattedId = formatOrderId(order.id).toLowerCase();
    const matchesSearch =
      String(order.id).toLowerCase().includes(query) ||
      // formattedId.includes(query) ||
      (order.customerName && order.customerName.toLowerCase().includes(query)) ||
      (order.amount && order.amount.toLowerCase().includes(query));

    // Date range filtering
    // const orderDate = new Date(order.date);
    // const from = fromDate ? new Date(fromDate) : null;
    // const to = toDate ? new Date(toDate) : null;
     const orderDate = parseDDMMYYYY(order.date);
    const from = fromDate ? parseYYYYMMDD(fromDate) : null;
    const to = toDate ? parseYYYYMMDD(toDate) : null;


    const matchesDateRange =
      (!from || orderDate >= from) &&
      (!to || orderDate <= to);

    // Status filtering
    const matchesStatus = !statusFilter || order.status === statusFilter;

    return matchesSearch && matchesDateRange && matchesStatus;
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentOrders,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredOrders, 10);

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearStatusFilter = () => {
    setStatusFilter("");
  };

  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
  };

  const pendingCount = currentOrders.filter(dn => dn.status == 0).length;
  const completedCount = currentOrders.filter(dn => dn.status == 1).length;
  const processCount = currentOrders.filter(dn => dn.status == 2).length;

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading orders...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchOrders}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Order Form Management</h2>

      {/* Filters Section */}
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          {/* Date Range Filters */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Form.Group>
          </Col>

          {/* Status Filter */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="0">Pending</option>
                <option value="2">Processing</option>
                {/* <option value="Shipped">Shipped</option> */}
                <option value="1">Completed</option>
                {/* <option value="Cancelled">Cancelled</option> */}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filter Actions */}
          <Col xs={12} md={6} lg={3} className="d-flex align-items-end gap-2">
            <Button
              variant="outline-secondary"
              onClick={clearDateFilter}
              disabled={!fromDate && !toDate}
              size="sm"
            >
              Clear Dates
            </Button>
            <Button
              variant="outline-secondary"
              onClick={clearAllFilters}
              disabled={!fromDate && !toDate && !statusFilter && !search}
              size="sm"
            >
              Clear All
            </Button>
          </Col>
        </Row>
      </div>

      {/* Search and Add Order Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search ID, customer name, amount"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
          <Link
            to="/addorder"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Order
          </Link>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-warning text-dark">
            <h4 className="mb-1 me-2">{pendingCount}</h4>
            <small>Pending</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-info text-white">
            <h4 className="mb-1 me-2">{processCount}</h4>
            <small>Processing</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-success text-white">
            <h4 className="mb-1 me-2">{completedCount}</h4>
            <small>Completed</small>
          </div>
        </Col>
        {/* */}




        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-primary text-white">
            <h4 className="mb-1 me-2">{currentOrders.length}</h4>
            <small>Total DN</small>
          </div>
        </Col>
      </Row>

      {/* Table Section */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                <th>Order ID</th>

                <th>Customer Name</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order, index) => (
                <tr key={order.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{order.date}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaFileAlt className="text-primary me-2" />
                      {order.orderId}
                    </div>
                  </td>

                  <td>{order.customerName}</td>
                  <td className="fw-bold right-align">{order.amount}</td>
                  <td>
                    <span className={getStatusClass(order.status)}>
                      {order.status == 0 ? "Pending" : order.status == 1 ? "Completed" : "Processing"}
                    </span>
                  </td>
                  <td>
                    <div className="action-icons">
                      <Link
                        to={`/vieworder/${order.id}`}
                        className="view-link"
                        title="View Order"
                      >
                        <FaEye className="view-icon" />
                      </Link>

                      {/* <Link
                        to={`/editorder/${order.id}`}
                        className="me-2"
                        title="Edit Order"
                      >
                        <FaEdit className="edit-icon" />
                      </Link> */}
                      <FaTrash
                        className="delete-icon"
                        title="Delete Order"
                        onClick={() => handleDelete(order.id, order.orderId)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {currentOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="text-muted">
                      <FaFileAlt size={48} className="mb-3" />
                      <p>No orders found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Footer */}
      <Row className="mt-4">
        <Col xs={12}>
          <div className="summary-footer p-3 border rounded bg-light">
            <Row>
              <Col md={4}>
                <strong>Total Orders:</strong> {filteredOrders.length}
              </Col>
              <Col md={4}>
                <strong>Total Amount:</strong> ₹{
                  filteredOrders.reduce((total, order) => {
                    return total + (parseFloat(order.grand_total) || 0);
                  }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                }
              </Col>
              <Col md={4}>
                <strong>Filtered Results:</strong> {filteredOrders.length} of {orders.length}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderList;